import clsx from 'clsx';
import { addHours, format } from 'date-fns';
import React, { useMemo, useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { ShiftTypesEnum } from '../../../../../js/api';
import { useWindowSize } from '../../../../../js/app/hooks';
import { Button } from '../../../components/button';
import { Field } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import { SHIFT_TYPE_LABELS, SHIFT_TYPE_ICONS } from '../../../utils/utils';
import type { ShiftSettings } from './ShiftConfiguration';
import { formatDateForTimeInput, parseTime, parseTimeInput } from './timeUtils';

interface DateShiftConfig {
  shiftType: ShiftTypesEnum | null;
  onCall: boolean;
}

export interface ShiftEntry {
  date: Date;
  dateKey: string;
  shiftType: ShiftTypesEnum;
  onCall: boolean;
  startTime: Date;
  endTime: Date;
}

interface SchedulePreviewProps {
  selectedDates: Date[];
  shiftConfigs: Record<string, DateShiftConfig>;
  shiftSettings: ShiftSettings;
  onBack: () => void;
  onRemoveDate: (dateKey: string) => void;
  onSubmit: (shiftEntries: ShiftEntry[], comment: string) => Promise<void>;
  isSubmitting?: boolean;
  submitError?: string | null;
  shiftErrors?: Record<number, string[]>;
}

// Calculate shift times based on shift type and shift settings
const calculateShiftTimes = (
  date: Date,
  shiftType: ShiftTypesEnum,
  shiftSettings: ShiftSettings
): { startTime: Date; endTime: Date } => {
  let startTimeStr: string;
  switch (shiftType) {
    case ShiftTypesEnum.DAY:
      startTimeStr = shiftSettings.shift_start_day;
      break;
    case ShiftTypesEnum.NIGHT:
      startTimeStr = shiftSettings.shift_start_night;
      break;
    case ShiftTypesEnum._24H:
      startTimeStr = shiftSettings.shift_start_24h;
      break;
    default:
      startTimeStr = shiftSettings.shift_start_day;
  }

  const { hours, minutes } = parseTime(startTimeStr);
  const shiftLength = shiftType === ShiftTypesEnum._24H ? 24 : shiftSettings.shift_length;

  // Create start time on the given date
  const startTime = new Date(date);
  startTime.setHours(hours, minutes, 0, 0);

  // Calculate end time by adding shift length hours
  const endTime = addHours(startTime, shiftLength);

  return { startTime, endTime };
};

interface ShiftTypeStyles {
  color: string;
  borderColor: string;
  backgroundColor: string;
}

const SHIFT_TYPE_STYLES: Record<ShiftTypesEnum, ShiftTypeStyles> = {
  [ShiftTypesEnum.DAY]: {
    color: 'tw:text-amber-700',
    borderColor: 'tw:border-amber-100',
    backgroundColor: 'tw:bg-amber-50',
  },
  [ShiftTypesEnum.NIGHT]: {
    color: 'tw:text-blue-700',
    borderColor: 'tw:border-blue-100',
    backgroundColor: 'tw:bg-blue-50',
  },
  [ShiftTypesEnum._24H]: {
    color: 'tw:text-green-700',
    borderColor: 'tw:border-green-100',
    backgroundColor: 'tw:bg-green-50',
  },
};

export const SchedulePreview: React.FC<SchedulePreviewProps> = ({
  selectedDates,
  shiftConfigs,
  shiftSettings,
  onBack,
  onRemoveDate,
  onSubmit,
  isSubmitting = false,
  submitError = null,
  shiftErrors = {},
}) => {
  const { width } = useWindowSize();
  const isSmallScreen = width < 868; // ml breakpoint

  const [comment, setComment] = useState('');

  // Generate initial shift entries from shiftConfigs
  const initialShiftEntries = useMemo<ShiftEntry[]>(() => {
    const entries: ShiftEntry[] = [];

    selectedDates.forEach((date) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const config = shiftConfigs[dateKey];

      if (config?.shiftType) {
        const { startTime, endTime } = calculateShiftTimes(date, config.shiftType, shiftSettings);

        entries.push({
          date,
          dateKey,
          shiftType: config.shiftType,
          onCall: config.onCall,
          startTime,
          endTime,
        });
      }
    });

    // Sort by date and start time
    return entries.sort((a, b) => {
      if (a.date.getTime() !== b.date.getTime()) {
        return a.date.getTime() - b.date.getTime();
      }
      return a.startTime.getTime() - b.startTime.getTime();
    });
  }, [selectedDates, shiftConfigs, shiftSettings]);

  // State to hold the shift entries (editable)
  const [shiftEntries, setShiftEntries] = useState<ShiftEntry[]>(initialShiftEntries);

  // Update shift entries when initial entries change (e.g., when dates/configs change)
  useEffect(() => {
    setShiftEntries(initialShiftEntries);
  }, [initialShiftEntries]);

  // Handle start time change
  const handleStartTimeChange = (dateKey: string, date: Date, value: string) => {
    const parsedTime = parseTimeInput(value, date);
    if (parsedTime) {
      setShiftEntries((prev) =>
        prev.map((entry) =>
          entry.dateKey === dateKey ? { ...entry, startTime: parsedTime } : entry
        )
      );
    }
  };

  // Handle end time change
  const handleEndTimeChange = (dateKey: string, date: Date, value: string) => {
    const parsedTime = parseTimeInput(value, date);
    if (parsedTime) {
      setShiftEntries((prev) =>
        prev.map((entry) => (entry.dateKey === dateKey ? { ...entry, endTime: parsedTime } : entry))
      );
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    await onSubmit(shiftEntries, comment);
  };

  return (
    <div className="tw:md:min-w-3xl tw:md:bg-white tw:md:rounded-xl tw:md:p-4 tw:md:border tw:md:border-zinc-200">
      <div className="tw:flex tw:justify-between tw:items-center tw:mb-4">
        <h2 className="tw:!text-base tw:!font-semibold tw:!text-brand-green-0 tw:!mb-0">
          Schedule Preview
        </h2>
        <Button type="button" color="brand-green-outline" onClick={onBack} small={isSmallScreen}>
          <i className="far fa-fw fa-arrow-left" />
          Back to Calendar
        </Button>
      </div>

      {submitError && (
        <div className="tw:mb-4 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded tw:text-red-700 tw:text-sm">
          {submitError}
        </div>
      )}

      <div className="tw:mb-4">
        {shiftEntries.map((entry, index) => {
          const dateFormattedLarge = format(entry.date, 'EEEE, MMMM d, yyyy');
          const dateFormattedSmall = format(entry.date, 'EEE MM/d/yyyy');
          // Format for time input (HH:mm)
          const startTimeInputValue = formatDateForTimeInput(entry.startTime);
          const endTimeInputValue = formatDateForTimeInput(entry.endTime);
          const shiftLabel = entry.onCall
            ? `${SHIFT_TYPE_LABELS[entry.shiftType]} Shift - Call`
            : `${SHIFT_TYPE_LABELS[entry.shiftType]} Shift`;
          const entryErrors = shiftErrors[index] || [];

          return (
            <div
              key={entry.dateKey}
              className="tw:flex tw:flex-col tw:items-center tw:justify-between tw:pb-2"
            >
              <div
                className={twMerge(
                  clsx(
                    'tw:flex tw:flex-col tw:w-full tw:p-3 tw:rounded-xl tw:border',
                    SHIFT_TYPE_STYLES[entry.shiftType].backgroundColor,
                    SHIFT_TYPE_STYLES[entry.shiftType].borderColor
                  )
                )}
              >
                <div className="tw:flex tw:items-center tw:justify-between">
                  {/* Date and Shift Type */}
                  <div className="tw:flex-1">
                    <p className="tw:hidden tw:md:block tw:text-sm tw:text-zinc-900 tw:font-medium tw:!mb-1">
                      {dateFormattedLarge}
                    </p>
                    <p className="tw:block tw:md:hidden tw:text-sm tw:text-zinc-900 tw:font-medium tw:!mb-1">
                      {dateFormattedSmall}
                    </p>
                    <p
                      className={twMerge(
                        'tw:text-sm tw:!mb-0',
                        SHIFT_TYPE_STYLES[entry.shiftType].color
                      )}
                    >
                      <i className={`fas ${SHIFT_TYPE_ICONS[entry.shiftType]} tw:mr-2`} />
                      {shiftLabel}
                    </p>
                  </div>
                  {/* Start and End Time */}
                  <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap tw:md:flex-2 tw:ml-3 tw:md:ml-0">
                    <Input
                      type="time"
                      value={startTimeInputValue}
                      onChange={(e) =>
                        handleStartTimeChange(entry.dateKey, entry.date, e.target.value)
                      }
                      className="tw:flex-1"
                      small={isSmallScreen}
                    />
                    <span className="tw:hidden tw:md:block tw:text-sm tw:text-zinc-500">to</span>
                    <Input
                      type="time"
                      value={endTimeInputValue}
                      onChange={(e) =>
                        handleEndTimeChange(entry.dateKey, entry.date, e.target.value)
                      }
                      className="tw:flex-1"
                      small={isSmallScreen}
                    />
                  </div>
                  {/* Remove Shift Button */}
                  <button
                    type="button"
                    className="hover:tw:text-red-700 tw:!ml-4"
                    aria-label="Remove shift"
                    onClick={() => onRemoveDate(entry.dateKey)}
                  >
                    <i className="far fa-fw fa-trash-can" />
                  </button>
                </div>
                {/* Shift Errors */}
                {entryErrors.length > 0 && (
                  <div className="tw:w-full tw:mt-2 tw:pl-3">
                    {entryErrors.map((errorMessage, errorIndex) => (
                      <p
                        key={errorIndex}
                        className="tw:text-sm tw:text-stoplight-danger tw:!mb-0 tw:text-right"
                      >
                        {errorMessage}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Field className="tw:mb-4">
        <Textarea
          placeholder="Add any additional notes or comments..."
          className="tw:w-full"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Field>

      <div className="tw:flex tw:justify-end">
        <Button
          type="button"
          color="brandgreen0"
          onClick={handleSubmit}
          disabled={isSubmitting || shiftEntries.length === 0}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Assignment'}
        </Button>
      </div>
    </div>
  );
};
